import { createFileRoute } from "@tanstack/react-router";
import { BookingProvider } from "@/components/site/booking-context";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { MobileBookingBar } from "@/components/site/MobileBookingBar";
import {
  AmenitiesHero,
  AmenitiesIntro,
  FeaturedAmenities,
  PetFriendly,
  OutdoorLiving,
  WifiSection,
  AmenitiesAtAGlance,
  DayAtAuburn,
  ConvenientLocation,
  SiteCrossSell,
  AmenitiesMidCTA,
  AmenitiesFAQ,
  AmenitiesDestinationBridge,
  AmenitiesCTA,
} from "@/components/site/amenities-page";
import { heroPreloadLink } from "@/data/optimized-images";
import { amenityFaqs, resort } from "@/data/resort";
import { absoluteUrl } from "@/lib/site";
import heroMainAsset from "@/assets/auburn-hero-main.webp.asset.json";


const title = "RV Resort Amenities in Auburn, CA | Auburn RV Resort";
const description =
  "Explore amenities at Auburn RV Resort in Auburn, California, including WiFi, laundry, bath and shower facilities, a fenced dog park, dog wash, picnic tables and BBQ area.";
const ogImage = `https://auburnrvresort.com${heroMainAsset.url}`;


const schema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["LodgingBusiness", "Campground"],
      name: resort.name,
      description,
      telephone: resort.phone,
      email: resort.email,
      url: "/amenities/",
      address: {
        "@type": "PostalAddress",
        streetAddress: resort.address.street,
        addressLocality: resort.address.city,
        addressRegion: resort.address.state,
        postalCode: resort.address.zip,
        addressCountry: "US",
      },
      petsAllowed: true,
      amenityFeature: [
        "On-Site Laundry",
        "Bath and Shower Facilities",
        "Fenced Dog Park",
        "Dog Wash Station",
        "Picnic Tables",
        "BBQ Area",
        "Complimentary WiFi",
      ].map((name) => ({
        "@type": "LocationFeatureSpecification",
        name,
        value: true,
      })),
    },
    {
      "@type": "FAQPage",
      mainEntity: amenityFaqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export const Route = createFileRoute("/amenities")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1322" },
      { property: "og:image:height", content: "936" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/amenities/") }, heroPreloadLink],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(schema) }],
  }),
  component: AmenitiesPage,
});

function AmenitiesPage() {
  return (
    <BookingProvider>
      <Navbar />
      <main>
        <AmenitiesHero />
        <AmenitiesIntro />
        <FeaturedAmenities />
        <PetFriendly />
        <OutdoorLiving />
        <WifiSection />
        <AmenitiesAtAGlance />
        <DayAtAuburn />
        <ConvenientLocation />
        <SiteCrossSell />
        <AmenitiesMidCTA />
        <AmenitiesFAQ />
        <AmenitiesDestinationBridge />
        <AmenitiesCTA />
      </main>
      <Footer />
      <MobileBookingBar />
    </BookingProvider>
  );
}
